import React, { useMemo } from 'react';

import Table from '../common/Table';
import { PermissionDTO } from '../../services/apiClient';

interface PermissionsTableProps {
  permissions: PermissionDTO[];
  selectedPermissions: string[];
  onPermissionChange: (permission: string, isSelected: boolean) => void;
}

interface GroupedPermissions {
  entity: string;
  category: string;
  permissions: Record<string, PermissionDTO>;
}

const splitPascalCase = (str: string): string => {
  return (
    str
      // Insert a space between lower & upper
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // Space before last upper in a sequence
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
      // Uppercase the first character
      .replace(/^./, (str) => str.toUpperCase())
  );
};

const PermissionsTable: React.FC<PermissionsTableProps> = ({
  permissions,
  selectedPermissions,
  onPermissionChange,
}) => {
  const availableActions = useMemo(() => {
    const actions = new Set<string>();
    permissions.forEach((permission) => {
      if (permission.action) {
        actions.add(permission.action);
      }
    });
    return Array.from(actions).sort();
  }, [permissions]);

  // Group permissions by entity and category
  const tableData = useMemo(() => {
    const grouped = permissions.reduce(
      (acc: GroupedPermissions[], permission) => {
        const entity = permission.entity || 'General';
        const category = permission.category || 'Other';

        let entityGroup = acc.find((g) => g.entity === entity);
        if (!entityGroup) {
          entityGroup = {
            entity,
            category,
            permissions: {},
          };
          acc.push(entityGroup);
        }

        entityGroup.permissions[permission.action || ''] = permission;
        return acc;
      },
      [],
    );

    return grouped;
  }, [permissions]);

  const renderCheckbox = (row: GroupedPermissions, action: string) => {
    const permission = row.permissions[action];
    if (!permission) return <></>;

    return (
      <input
        type="checkbox"
        checked={selectedPermissions.includes(permission.name || '')}
        onChange={(e) =>
          onPermissionChange(permission.name || '', e.target.checked)
        }
        className="form-checkbox h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
      />
    );
  };

  return (
    <Table
      allowPaging={false}
      columns={[
        {
          label: '',
          key: 'entity',
          format: (value) => splitPascalCase(value),
        },
        // Dynamically create columns for each action
        ...availableActions.map(action => ({
            label: splitPascalCase(action),
            key: action.toLowerCase(),
            render: (row: GroupedPermissions) => renderCheckbox(row, action),
          })),
      ]}
      data={tableData}
      groupBy={['category']}
      groupHeaderRender={(group) => (
        <div className="flex items-center">
          <span className="text-sm text-gray-800 dark:text-cream">
            {group}
          </span>
        </div>
      )}
      currentPage={1}
      totalPages={1}
      pageSize={tableData.length}
      onPageChange={() => {}}
      onPageSizeChange={() => {}}
      isLoading={false}
    />
  );
};

export default PermissionsTable;
